import { useEffect, useState, type ReactNode } from 'react'
import { ArrowRight, Package, ShoppingCart, Storefront } from '@phosphor-icons/react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { CnpjIePriceLearnMore } from '@/components/pricing/CnpjIePriceLearnMore'
import { DistributionBenefitPreview } from '@/components/pricing/DistributionBenefitPreview'
import { usePricingContext } from '@/components/pricing/PricingContext'
import { useStorefrontSiteConfig } from '@/contexts/site-config'
import {
  isProductDisplayedPricePending,
  isProductPendingTechnicalPrice,
  resolveProductPricing,
  shouldPreviewDistributionBenefitPrice,
} from '@/lib/pricing'
import { canShowMarketplaceAdvertiserPrice } from '@/lib/marketplace-vitrine-price'
import { resolveProductStockDisplay } from '@/lib/product-stock'
import { BOX_COMPARE_AT_PRICE_CLASS, BOX_CURRENT_PRICE_CLASS, resolveBoxCurrentPriceStyle } from '@/lib/box-price-presentation'
import { darkenHexColor, hexToRgba } from '@/lib/storefront-theme'
import { cn } from '@/lib/utils'
import { requiresProductVariationSelection } from '@/lib/utils'
import { isSimplifiedStorefrontEnabled, requiresSimplifiedStorefrontProductConfiguration } from '@/lib/simplified-storefront'
import { handleLinkClick } from '@/lib/navigation'
import { PRODUCT_IMAGE_PLACEHOLDER_URL, normalizeProductImageList, resolveProductImageUrl } from '@/lib/product-image-placeholder'
import {
  trackStorefrontImportantClick,
  trackStorefrontSearchResultClick,
  type StorefrontSearchResultContext,
} from '@/lib/storefront-analytics'
import type { Product } from '@/lib/types'
import { useTranslation } from 'react-i18next'
import { formatCommerceCurrency, useBuyerCommerceContext } from '@/lib/commerce-context'

const currencyFormatter = { format: formatCommerceCurrency }

const PRICE_PANEL_CLASS = 'rounded-lg border'
const PRICE_PANEL_COMPACT_CLASS = 'flex flex-col items-end gap-0.5 rounded-md border px-2 py-1'
const PRICE_PANEL_BADGE_CLASS = 'inline-flex w-fit max-w-full items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none whitespace-nowrap'
const PRICE_PANEL_BADGE_TIGHT_CLASS = 'inline-flex w-fit max-w-full items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold leading-none tracking-[-0.01em] whitespace-nowrap'
const PRICE_PANEL_BADGE_COMPACT_CLASS = 'inline-flex w-fit max-w-full items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold leading-none tracking-[-0.01em] whitespace-nowrap'
const BOX_PRODUCT_CARD_CLASS = 'border-amber-300 bg-gradient-to-br from-amber-50/90 via-white to-orange-50/70 ring-1 ring-amber-200/60'
const BOX_PRODUCT_BADGE_CLASS = 'rounded-full border border-amber-300 bg-amber-100 px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-amber-800 shadow-sm'

const isFinitePositiveNumber = (value: number | null | undefined): value is number => (
  typeof value === 'number' && Number.isFinite(value) && value > 0
)

const normalizeBoxCardPrice = (
  product: Pick<Product, 'packagingMode' | 'packagingQuantity' | 'unitPrice'>,
  value: number | null | undefined,
) => {
  if (!isFinitePositiveNumber(value)) return null

  const packagingQuantity = Math.max(1, Math.trunc(product.packagingQuantity ?? 0))
  if (product.packagingMode !== 'box' || packagingQuantity <= 1) return value

  const unitReference = isFinitePositiveNumber(product.unitPrice) ? product.unitPrice : null
  if (unitReference !== null) {
    if (Math.abs(value - unitReference) < 0.0001) return value
    if (value > unitReference * 1.5) {
      return Number((value / packagingQuantity).toFixed(4))
    }
    return value
  }

  return Number((value / packagingQuantity).toFixed(4))
}

interface ProductCardProps {
  product: Product
  onAddToCart: (product: Product) => void
  onClick: (product: Product) => void
  compact?: boolean
  onAddToCompare?: (product: Product) => void
  isInCompare?: boolean
  viewMode?: 'grid' | 'list' | 'compact'
  isLoggedIn?: boolean
  allowGuestCheckout?: boolean
  maxNameLines?: 2 | 3
  imageAlign?: 'center' | 'top'
  hideLoginBadge?: boolean
  flushImage?: boolean
  linkHref?: string
  showRatings?: boolean
  showStockBadge?: boolean
  topRightSlot?: ReactNode
  priorityImage?: boolean
  narrowPrice?: boolean
  searchResultContext?: StorefrontSearchResultContext | null
}

export function ProductCard({
  product,
  onAddToCart,
  onClick,
  compact = false,
  viewMode = 'grid',
  isLoggedIn = false,
  allowGuestCheckout = false,
  maxNameLines = 2,
  imageAlign = 'center',
  hideLoginBadge = false,
  flushImage = false,
  linkHref,
  showRatings = false,
  showStockBadge = false,
  topRightSlot,
  priorityImage = false,
  narrowPrice = false,
  searchResultContext = null,
}: ProductCardProps) {
  useBuyerCommerceContext()
  const { t } = useTranslation()
  const pricingContext = usePricingContext()
  const { themeColors, useAutoPartsPlaceholder } = useStorefrontSiteConfig()
  const pricing = resolveProductPricing(product, pricingContext)
  const stockDisplay = resolveProductStockDisplay(product, pricingContext.showRealStock, pricingContext.stockStatusMode)
  const showPrices = pricingContext.showPrices
  const canDisplayPrice = showPrices && stockDisplay.available
  const canDisplayGuestPricePrompt = !showPrices && stockDisplay.available
  const titleClampClass = maxNameLines === 3 ? 'line-clamp-3' : 'line-clamp-2'
  const titleMinHeightClass = maxNameLines === 3
    ? (flushImage ? 'min-h-[3.1rem]' : 'min-h-[3.6rem]')
    : (flushImage ? 'min-h-[2.05rem]' : 'min-h-[2.4rem]')
  const listTitleClampClass = maxNameLines === 3 ? 'line-clamp-3' : 'line-clamp-2'
  const imagePaddingClass = flushImage ? 'p-2' : imageAlign === 'top' ? 'pt-0 px-6 pb-6' : 'p-6'
  const imagePositionClass = imageAlign === 'top' ? 'object-top' : 'object-center'
  const simplifiedStorefrontEnabled = isSimplifiedStorefrontEnabled()
  const normalizedProductImages = normalizeProductImageList([
    product.image,
    ...(Array.isArray(product.images) ? product.images : []),
  ])
  const productImageCandidates = simplifiedStorefrontEnabled
    ? normalizedProductImages
    : normalizedProductImages.slice(0, 1)
  const productImageCandidatesKey = productImageCandidates.join('\n')
  const [productImageCandidateIndex, setProductImageCandidateIndex] = useState(0)
  const productImageSrc = resolveProductImageUrl(
    productImageCandidates[productImageCandidateIndex],
    { includePlaceholder: useAutoPartsPlaceholder },
  )
  const [hasImageError, setHasImageError] = useState(false)
  const cardPaddingClass = flushImage ? 'p-0' : ''
  const contentPaddingClass = flushImage ? 'px-3 pb-3 pt-2' : 'p-3'
  const contentGapClass = flushImage ? 'gap-1.5' : 'gap-2'
  const titleSpacingClass = flushImage ? 'mt-1' : ''
  const isLinkedCard = Boolean(linkHref)
  const requiresVariationSelection = requiresProductVariationSelection(product)
    || requiresSimplifiedStorefrontProductConfiguration(product)
  const isPricePending = isProductDisplayedPricePending(product, pricingContext, pricing)
  const lowPrice = pricing.actualPrice
  const highPrice = pricing.compareAtPrice
  const canShowRealPrice = !isPricePending && pricing.canShowPrice
  const packagingQuantity = Math.max(1, Math.trunc(product.packagingQuantity ?? 0))
  const isBoxPackaging = product.packagingMode === 'box' && packagingQuantity > 1
  const displayedPrice = normalizeBoxCardPrice(product, lowPrice)
  const displayedCompareAtPrice = normalizeBoxCardPrice(product, highPrice)
  const displayedPotentialPriceCandidate = normalizeBoxCardPrice(product, pricing.potentialPrice)
  const hasPendingPotentialPricePlaceholder = isProductPendingTechnicalPrice(
    product,
    displayedPotentialPriceCandidate,
  )
  const displayedPotentialPrice = hasPendingPotentialPricePlaceholder
    ? undefined
    : displayedPotentialPriceCandidate
  const showDistributionBenefitPreview = !hasPendingPotentialPricePlaceholder
    && shouldPreviewDistributionBenefitPrice(pricing)
  const pricePrefix = isBoxPackaging
    ? t('product.unit_price_label')
    : product.hasVariations
      ? t('product.price_from')
      : t('product.price_for')
  const priceLabel = canShowRealPrice && typeof displayedPrice === 'number'
    ? currencyFormatter.format(displayedPrice)
    : ''
  const hasDiscount = pricing.hasCompareAtPrice && !showDistributionBenefitPreview
  const discountPercent = pricing.discountPercent
  const listPriceLabel = hasDiscount && typeof displayedCompareAtPrice === 'number'
    ? currencyFormatter.format(displayedCompareAtPrice)
    : ''
  const isDistributionPriceActive = pricing.displayDistributionBenefit
  const useCompactDistributionBadge = flushImage || compact || viewMode === 'compact'
  const distributionPriceBadgeLabel = useCompactDistributionBadge
    ? t('product.distribution_price_active_compact')
    : t('product.distribution_price_active')
  const distributionLostBenefitPotentialPrice = showDistributionBenefitPreview && typeof displayedPotentialPrice === 'number'
    ? currencyFormatter.format(displayedPotentialPrice)
    : ''
  const potentialPriceLabel = !showDistributionBenefitPreview && displayedPotentialPrice
    ? currencyFormatter.format(displayedPotentialPrice)
    : ''
  const potentialPriceHint = !showDistributionBenefitPreview && pricing.potentialPriceKind === 'distribution'
    ? t('product.distribution_potential_price', { value: potentialPriceLabel })
    : pricing.potentialPriceKind === 'cnpj_ie_no_difal'
      ? t('product.cnpj_ie_no_difal')
    : pricing.potentialPriceKind === 'cnpj_ie'
      ? <CnpjIePriceLearnMore value={potentialPriceLabel} />
      : null
  const priceHintReserveClass = flushImage ? 'min-h-[3.35rem]' : 'min-h-[3.7rem]'
  const alignedPriceAreaClass = 'flex flex-col'
  const defaultPriceValueClass = narrowPrice
    ? 'text-[1.38rem]'
    : 'text-[1.6rem]'
  const distributionPreviewPriceClass = narrowPrice
    ? 'text-[1.38rem]'
    : undefined
  const guestPricingAreaClass = hideLoginBadge
    ? 'mt-auto'
    : 'mt-auto flex flex-col gap-2'
  const pendingPricePlaceholder = (
    <div
      role="status"
      aria-label="Calculando preço"
      className="flex min-h-10 w-full max-w-40 animate-pulse flex-col gap-2 py-1"
    >
      <span className="h-2 w-16 rounded bg-muted" />
      <span className="h-6 w-28 rounded bg-muted" />
      <span className="sr-only">Calculando preço</span>
    </div>
  )
  const ratingStars = (
    <div className="flex items-center">
      {[...Array(5)].map((_, i) => (
        <svg key={i} className="w-3 h-3 fill-[#ff8e18]" viewBox="0 0 20 20">
          <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
        </svg>
      ))}
    </div>
  )
  const primaryActionLabel = requiresVariationSelection
    ? t('actions.view_product')
    : t('actions.add_to_cart')
  const canAddToCart = isLoggedIn || allowGuestCheckout
  const handleProductCardClick = () => {
    trackStorefrontSearchResultClick(searchResultContext, 'open')
    const normalizedProductId = Number.parseInt(product.id, 10)
    trackStorefrontImportantClick({
      eventKey: 'product_card_click',
      ...(Number.isFinite(normalizedProductId) && normalizedProductId > 0
        ? { produtoId: normalizedProductId }
        : {}),
      payload: {
        product_name: product.name,
        product_brand: product.brand || undefined,
        product_category: product.category || undefined,
        view_mode: compact ? 'compact' : viewMode,
      },
    })
    onClick(product)
  }
  const handlePrimaryAction = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (requiresVariationSelection) {
      trackStorefrontSearchResultClick(searchResultContext, 'open')
      onClick(product)
      return
    }

    if (stockDisplay.available && !isPricePending) {
      trackStorefrontSearchResultClick(searchResultContext, 'add_to_cart')
      onAddToCart(product)
    }
  }
  const handleProductImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    if (simplifiedStorefrontEnabled && productImageCandidateIndex + 1 < productImageCandidates.length) {
      setProductImageCandidateIndex((current) => current + 1)
      return
    }

    if (useAutoPartsPlaceholder && !event.currentTarget.src.endsWith(PRODUCT_IMAGE_PLACEHOLDER_URL)) {
      event.currentTarget.src = PRODUCT_IMAGE_PLACEHOLDER_URL
      return
    }

    setHasImageError(true)
  }

  const imageLoading = priorityImage ? 'eager' : 'lazy'
  const imageFetchPriority = priorityImage ? 'high' : 'auto'
  const renderProductImage = ({
    className,
    fallbackIconClassName = 'h-10 w-10',
    fetchPriority,
    height,
    loading,
    width,
  }: {
    className: string
    fallbackIconClassName?: string
    fetchPriority?: 'high' | 'low' | 'auto'
    height?: number
    loading?: 'eager' | 'lazy'
    width?: number
  }) => {
    if (productImageSrc && !hasImageError) {
      return (
        <img
          src={productImageSrc}
          alt={product.name}
          width={width}
          height={height}
          loading={loading}
          fetchPriority={fetchPriority}
          decoding="async"
          draggable={false}
          onError={handleProductImageError}
          className={className}
        />
      )
    }

    return (
      <div
        role="img"
        aria-label={product.name}
        className="flex h-full w-full items-center justify-center text-muted-foreground/35"
      >
        <Package className={fallbackIconClassName} weight="duotone" />
      </div>
    )
  }
  const priceCardStyle = {
    borderColor: hexToRgba(themeColors.priceText, 0.16),
    backgroundColor: hexToRgba(themeColors.priceCard, 0.72),
  }
  const priceBadgeStyle = {
    backgroundColor: hexToRgba(themeColors.priceText, 0.12),
    color: themeColors.priceText,
  }
  const priceValueStyle = { color: themeColors.priceText }
  const priceHintStyle = { color: darkenHexColor(themeColors.priceText, 0.08) }
  const buyButtonStyle = {
    backgroundColor: themeColors.buyButton,
    color: themeColors.buyButtonForeground,
    boxShadow: `0 6px 12px -12px ${hexToRgba(themeColors.buyButton, 0.4)}`,
  }
  const addToCartButtonStyle = {
    borderColor: hexToRgba(themeColors.addToCartButton, 0.22),
    backgroundColor: themeColors.addToCartButton,
    color: themeColors.addToCartButtonForeground,
    boxShadow: `0 6px 12px -12px ${hexToRgba(themeColors.addToCartButton, 0.4)}`,
  }
  const primaryActionStyle = requiresVariationSelection ? buyButtonStyle : addToCartButtonStyle
  const stockBadgeClassName = stockDisplay.available
    ? 'border-green-500/20 bg-green-500/10 text-green-700 hover:bg-green-500/10'
    : 'border-red-500/20 bg-red-500/10 text-red-700 hover:bg-red-500/10'
  const unavailablePriceArea = (
    <div className="mt-auto flex flex-col">
      <Badge className={cn('w-fit rounded-full border px-2 py-0.5 text-xs font-semibold', stockBadgeClassName)}>
        {stockDisplay.label}
      </Badge>
    </div>
  )

  useEffect(() => {
    setProductImageCandidateIndex(0)
    setHasImageError(false)
  }, [productImageCandidatesKey])

  if (product.origem === 'marketplace_vitrine') {
    const canShowMarketplacePrice = canShowMarketplaceAdvertiserPrice(isLoggedIn)
    const marketplacePrice = canShowMarketplacePrice
      ? typeof product.price === 'number' && product.price > 0
        ? currencyFormatter.format(product.price)
        : 'Consulte o vendedor'
      : t('product.login_to_see_prices')
    const marketplacePricePresentation = canShowMarketplacePrice ? (
      <div className={`px-2.5 py-2 ${PRICE_PANEL_CLASS}`} style={priceCardStyle}>
        <div className="mb-1">
          <span className="text-[10px] font-semibold uppercase tracking-wide" style={priceHintStyle}>
            {t('product.price_from')}
          </span>
        </div>
        <div className="flex items-baseline gap-1">
          <span
            className={cn('font-price text-[1.6rem] font-bold leading-none whitespace-nowrap tabular-nums', defaultPriceValueClass)}
            style={priceValueStyle}
          >
            {marketplacePrice}
          </span>
        </div>
      </div>
    ) : (
      <div className="text-xs text-slate-500">{marketplacePrice}</div>
    )
    const marketplaceCard = (
      <Card
        className={cn(
          'group h-full cursor-pointer overflow-hidden border border-violet-200 bg-white transition-all hover:shadow-md',
          viewMode === 'list' ? 'p-4' : 'flex flex-col gap-0',
        )}
        onClick={isLinkedCard ? undefined : handleProductCardClick}
      >
        <div className={cn(viewMode === 'list' ? 'grid gap-4 sm:grid-cols-[8rem_minmax(0,1fr)_14rem] sm:items-center' : 'contents')}>
          <div className={cn('relative flex justify-center overflow-hidden bg-slate-50', viewMode === 'list' ? 'h-32 items-center rounded-xl' : 'aspect-square w-full items-start')}>
            <div className="absolute inset-0">
              {renderProductImage({
                className: '-translate-y-8 h-full w-full object-contain object-top transition-transform duration-300 group-hover:scale-105',
                fallbackIconClassName: 'h-12 w-12',
              })}
            </div>
          </div>
          <div className={cn('min-w-0', viewMode === 'list' ? '' : 'flex flex-1 flex-col p-3')}>
            <h3 className="line-clamp-2 text-sm font-semibold leading-5 text-slate-950 group-hover:text-violet-700">{product.name}</h3>
            <p className="mt-1 text-xs text-slate-500">Código {product.code}</p>
            <div className="mt-3 flex items-center gap-2 text-xs font-semibold text-slate-600">
              <Storefront className="h-4 w-4 text-violet-600" weight="duotone" />
              <span className="line-clamp-1">Vendido por {product.marketplaceSellerName || 'cliente'}</span>
            </div>
            {viewMode !== 'list' ? (
              <div className="mt-auto pt-4">
                {marketplacePricePresentation}
                <div className={`mt-2 flex items-center gap-1.5 text-xs font-bold ${product.inStock ? 'text-emerald-700' : 'text-red-600'}`}>
                  {product.inStock ? <span aria-hidden="true" className="h-2 w-2 rounded-full bg-emerald-500" /> : null}
                  {product.inStock ? 'Disponível' : 'Esgotado'}
                </div>
              </div>
            ) : null}
          </div>
          {viewMode === 'list' ? (
            <div className="flex flex-col gap-3 sm:items-end">
              {marketplacePricePresentation}
              <div className={`flex items-center gap-1.5 text-xs font-bold ${product.inStock ? 'text-emerald-700' : 'text-red-600'}`}>
                {product.inStock ? <span aria-hidden="true" className="h-2 w-2 rounded-full bg-emerald-500" /> : null}
                {product.inStock ? 'Disponível' : 'Esgotado'}
              </div>
            </div>
          ) : null}
        </div>
      </Card>
    )

    if (isLinkedCard && linkHref) {
      return <a href={linkHref} className="block h-full" onClick={(event) => handleLinkClick(event, handleProductCardClick)}>{marketplaceCard}</a>
    }
    return marketplaceCard
  }
  
  if (compact || viewMode === 'grid') {
    const cardBody = (
      <Card
        className={cn(
          `group flex h-full flex-col cursor-pointer transition-all hover:shadow-md overflow-hidden border border-border/60 bg-white relative gap-0 ${cardPaddingClass}`,
          isBoxPackaging && BOX_PRODUCT_CARD_CLASS,
        )}
        onClick={isLinkedCard ? undefined : handleProductCardClick}
      >
        {topRightSlot}
        {isBoxPackaging ? (
          <Badge className={cn('absolute left-2 top-2 z-10', BOX_PRODUCT_BADGE_CLASS)}>
            {t('product.variations.packaging_badge_box')}
          </Badge>
        ) : null}
        <div className="relative aspect-square w-full overflow-hidden bg-[#fafafa]">
          {renderProductImage({
            width: 640,
            height: 640,
            loading: imageLoading,
            fetchPriority: imageFetchPriority,
            className: `h-full w-full object-contain ${imagePositionClass} ${imagePaddingClass} group-hover:scale-105 transition-transform duration-300`,
          })}
        </div>

        <div className={`flex flex-1 flex-col ${contentPaddingClass} ${contentGapClass}`}>
          <h3 className={`font-normal text-[13px] leading-tight ${titleClampClass} ${titleMinHeightClass} ${titleSpacingClass} text-foreground/95 group-hover:text-accent transition-colors`}>
            {product.name}
          </h3>
          {showRatings && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground/70">
              {ratingStars}
            </div>
          )}
          {showStockBadge && stockDisplay.visible && stockDisplay.available ? (
            <Badge className={cn('w-fit rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-none', stockBadgeClassName)}>
              {stockDisplay.label}
            </Badge>
          ) : null}
          
          {canDisplayPrice ? (
            <>
              <div className="mt-auto flex flex-col">
                {canShowRealPrice ? (
                  <>
                    {showDistributionBenefitPreview ? (
                      <div className={`mt-1 ${alignedPriceAreaClass}`}>
                        <DistributionBenefitPreview
                          label={pricePrefix}
                          price={priceLabel}
                          potentialPrice={distributionLostBenefitPotentialPrice}
                          priceClassName={distributionPreviewPriceClass}
                        />
                      </div>
                    ) : (
                      <div className={`mt-1 ${alignedPriceAreaClass}`}>
                        <div className={`px-2.5 py-2 ${PRICE_PANEL_CLASS}`} style={priceCardStyle}>
                          {hasDiscount ? (
                            <div className="mb-1 flex items-center gap-1.5">
                              <span className={cn(
                                'text-[11px]',
                                isBoxPackaging ? BOX_COMPARE_AT_PRICE_CLASS : 'text-muted-foreground/70 line-through',
                              )}>
                                {listPriceLabel}
                              </span>
                              <span className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold" style={priceBadgeStyle}>
                                -{discountPercent}%
                              </span>
                            </div>
                          ) : null}
                          <div className="mb-1">
                            <span className="text-[10px] font-semibold uppercase tracking-wide" style={priceHintStyle}>
                              {pricePrefix}
                            </span>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span
                              className={cn(
                                'font-price font-bold leading-none whitespace-nowrap tabular-nums',
                                defaultPriceValueClass,
                                isBoxPackaging && BOX_CURRENT_PRICE_CLASS,
                              )}
                              style={resolveBoxCurrentPriceStyle(priceValueStyle, isBoxPackaging)}
                            >
                              {priceLabel}
                            </span>
                            {isBoxPackaging ? (
                              <span className="text-[10px] font-semibold uppercase tracking-wide" style={priceHintStyle}>
                                {t('product.unit_price_suffix')}
                              </span>
                            ) : null}
                          </div>
                          {isDistributionPriceActive ? (
                            <div className="pt-1">
                              <span
                                className={flushImage ? PRICE_PANEL_BADGE_TIGHT_CLASS : PRICE_PANEL_BADGE_CLASS}
                                style={priceBadgeStyle}
                              >
                                {distributionPriceBadgeLabel}
                              </span>
                            </div>
                          ) : null}
                        </div>
                        {potentialPriceHint ? (
                          <div className={`mt-1 ${priceHintReserveClass}`}>
                            <p className="text-[11px] font-medium leading-relaxed" style={priceHintStyle}>
                              {potentialPriceHint}
                            </p>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </>
                ) : isPricePending ? (
                  pendingPricePlaceholder
                ) : (
                  <div className="mt-1 flex flex-1 items-start">
                    <p className="text-xs text-muted-foreground/80">
                      -
                    </p>
                  </div>
                )}
              </div>

              {!showRatings && (
                <div className="flex items-center gap-1 mt-1">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-3 h-3 fill-[#ff8e18]" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                  <span className="text-xs text-muted-foreground/70">| {t('product.sold_count', { count: 91 })}</span>
                </div>
              )}
            </>
          ) : canDisplayGuestPricePrompt ? (
            <div className={guestPricingAreaClass}>
              {!hideLoginBadge && (
                <Badge variant="outline" className="text-xs w-fit">
                  {t('product.price_after_login')}
                </Badge>
              )}
              <p className="text-xs text-muted-foreground">
                {t('product.login_to_see_prices')}
              </p>
            </div>
          ) : unavailablePriceArea}
        </div>
      </Card>
    )

    if (isLinkedCard && linkHref) {
      return (
        <a
          href={linkHref}
          className="block h-full"
          aria-label={product.name}
          onClick={(event) => handleLinkClick(event, handleProductCardClick)}
        >
          {cardBody}
        </a>
      )
    }

    return cardBody
  }

  if (viewMode === 'compact') {
    const cardBody = (
      <Card
        className={cn(
          'group flex cursor-pointer gap-3 p-3 transition-all hover:shadow-sm border border-border/60 bg-white relative',
          isBoxPackaging && BOX_PRODUCT_CARD_CLASS,
        )}
        onClick={isLinkedCard ? undefined : handleProductCardClick}
      >
        {topRightSlot}
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden bg-[#fafafa]">
          {renderProductImage({
            className: 'h-full w-full object-contain p-2',
            fallbackIconClassName: 'h-7 w-7',
          })}
        </div>

        <div className="flex flex-1 items-center gap-3 min-w-0">
          <div className="flex-1 min-w-0">
            {isBoxPackaging ? (
              <Badge className={cn('mb-1.5', BOX_PRODUCT_BADGE_CLASS)}>
                {t('product.variations.packaging_badge_box')}
              </Badge>
            ) : null}
            <h3 className="font-medium text-xs leading-tight text-foreground/95 line-clamp-1 group-hover:text-accent transition-colors">
              {product.name}
            </h3>
            <p className="text-[10px] text-muted-foreground/70 mt-0.5">
              {t('product.code_label')} {product.code}
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 rounded-full">
              {product.brand}
            </Badge>
            {stockDisplay.visible ? (
              <Badge className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                stockDisplay.available
                  ? 'bg-green-500/10 text-green-700 border-green-500/20'
                  : 'bg-red-500/10 text-red-700 border-red-500/20'
              }`}>
                {stockDisplay.label}
              </Badge>
            ) : null}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {canDisplayPrice ? canShowRealPrice ? (
              showDistributionBenefitPreview ? (
                <DistributionBenefitPreview
                  label={pricePrefix}
                  price={priceLabel}
                  potentialPrice={distributionLostBenefitPotentialPrice}
                  variant="mini"
                  align="right"
                  className="max-w-[10rem]"
                />
              ) : (
                <div className="flex flex-col items-end gap-0.5">
                  <div className={PRICE_PANEL_COMPACT_CLASS} style={priceCardStyle}>
                  {hasDiscount ? (
                    <div className="flex items-center gap-1">
                      <span className={cn(
                        'text-[10px]',
                        isBoxPackaging ? BOX_COMPARE_AT_PRICE_CLASS : 'text-muted-foreground/70 line-through',
                      )}>
                        {listPriceLabel}
                      </span>
                      <span className="rounded-full px-1 py-0.5 text-[9px] font-semibold" style={priceBadgeStyle}>
                        -{discountPercent}%
                      </span>
                    </div>
                  ) : null}
                  <span className="text-[9px] font-semibold uppercase tracking-wide" style={priceHintStyle}>
                    {pricePrefix}
                  </span>
                  <div className="flex items-baseline gap-1">
                    <span
                      className={cn('text-sm leading-none', isBoxPackaging ? BOX_CURRENT_PRICE_CLASS : 'font-bold')}
                      style={resolveBoxCurrentPriceStyle(priceValueStyle, isBoxPackaging)}
                    >
                      {priceLabel}
                    </span>
                    {isBoxPackaging ? (
                      <span className="text-[9px] font-semibold uppercase tracking-wide" style={priceHintStyle}>
                        {t('product.unit_price_suffix')}
                      </span>
                    ) : null}
                  </div>
                  {isDistributionPriceActive ? (
                    <span className={PRICE_PANEL_BADGE_COMPACT_CLASS} style={priceBadgeStyle}>
                      {distributionPriceBadgeLabel}
                    </span>
                  ) : null}
                  </div>
                  {potentialPriceHint ? (
                    <p className="max-w-[10rem] text-right text-[10px] font-medium leading-tight" style={priceHintStyle}>
                      {potentialPriceHint}
                    </p>
                  ) : null}
                </div>
              )
            ) : isPricePending ? (
              pendingPricePlaceholder
            ) : (
              <span className="text-xs text-muted-foreground/70">
                -
              </span>
            ) : canDisplayGuestPricePrompt && !hideLoginBadge ? (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0.5">
                {t('product.sign_in_to_see_price')}
              </Badge>
            ) : null}
            {canAddToCart ? (
              <Button
                size="sm"
                className="h-8 w-8 rounded-lg border p-0 shadow-sm transition-opacity duration-200 hover:opacity-90"
                style={{
                  borderColor: hexToRgba(themeColors.addToCartButton, 0.28),
                  backgroundColor: themeColors.addToCartButton,
                  color: themeColors.addToCartButtonForeground,
                }}
                onClick={handlePrimaryAction}
                disabled={!requiresVariationSelection && (!stockDisplay.available || isPricePending)}
                aria-label={primaryActionLabel}
                title={primaryActionLabel}
              >
                {requiresVariationSelection ? (
                  <ArrowRight className="h-3.5 w-3.5" />
                ) : (
                  <ShoppingCart className="h-3.5 w-3.5" />
                )}
              </Button>
            ) : null}
          </div>
        </div>
      </Card>
    )

    if (isLinkedCard && linkHref) {
      return (
        <a
          href={linkHref}
          className="block h-full"
          aria-label={product.name}
          onClick={(event) => handleLinkClick(event, handleProductCardClick)}
        >
          {cardBody}
        </a>
      )
    }

    return cardBody
  }

  const cardBody = (
    <Card
      className={cn(
        'group relative cursor-pointer border border-border/60 bg-white p-4 transition-all hover:shadow-sm',
        isBoxPackaging && BOX_PRODUCT_CARD_CLASS,
      )}
      onClick={isLinkedCard ? undefined : handleProductCardClick}
    >
      {topRightSlot}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[7.5rem_minmax(0,1fr)] lg:grid-cols-[7.5rem_minmax(0,1fr)_15rem] lg:items-center">
        <div className="flex h-[7.5rem] w-[7.5rem] shrink-0 items-center justify-center overflow-hidden rounded-md border border-border/50 bg-[#fafafa]">
          {renderProductImage({
            className: 'h-full w-full object-contain p-2.5',
          })}
        </div>

        <div className="min-w-0 space-y-2">
          {isBoxPackaging ? (
            <Badge className={BOX_PRODUCT_BADGE_CLASS}>
              {t('product.variations.packaging_badge_box')}
            </Badge>
          ) : null}
          <h3 className={`font-medium text-base leading-tight text-foreground/95 ${listTitleClampClass} group-hover:text-accent transition-colors`}>
            {product.name}
          </h3>
          <p className="text-sm text-muted-foreground/75">
            {t('product.code_label')} {product.code} | {product.manufacturerCode}
          </p>

          <div className="flex flex-wrap gap-1.5">
            <Badge variant="secondary" className="rounded-full text-xs">{product.brand}</Badge>
            <Badge variant="secondary" className="rounded-full text-xs">{product.category}</Badge>
            {stockDisplay.visible ? (
              <Badge className={`rounded-full text-xs ${
                stockDisplay.available
                  ? 'border-green-500/20 bg-green-500/10 text-green-700'
                  : 'border-red-500/20 bg-red-500/10 text-red-700'
              }`}>
                {stockDisplay.label}
              </Badge>
            ) : null}
          </div>

          <div className="flex items-center gap-1 pt-0.5">
            <div className="flex items-center">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="h-3 w-3 fill-[#ff8e18]" viewBox="0 0 20 20">
                  <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                </svg>
              ))}
            </div>
            <span className="text-xs text-muted-foreground/70">{t('product.sold_count', { count: 91 })}</span>
          </div>
        </div>

        <div className="flex flex-col gap-2 lg:items-end">
          {canDisplayPrice ? (
            <>
              {canShowRealPrice ? (
                <div className="w-full space-y-1 lg:w-[15rem]">
                  {showDistributionBenefitPreview ? (
                    <DistributionBenefitPreview
                      label={pricePrefix}
                      price={priceLabel}
                      potentialPrice={distributionLostBenefitPotentialPrice}
                      variant="compact"
                      align="right"
                    />
                  ) : (
                    <>
                      <div className={`px-3 py-2 ${PRICE_PANEL_CLASS}`} style={priceCardStyle}>
                      {hasDiscount ? (
                        <div className="mb-1 flex items-center gap-1.5">
                          <span className={cn(
                            'text-xs',
                            isBoxPackaging ? BOX_COMPARE_AT_PRICE_CLASS : 'text-muted-foreground/70 line-through',
                          )}>{listPriceLabel}</span>
                          <span className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold" style={priceBadgeStyle}>
                            -{discountPercent}%
                          </span>
                        </div>
                      ) : null}
                      <div className="mb-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wide" style={priceHintStyle}>
                          {pricePrefix}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span
                          className={cn('text-3xl leading-none', isBoxPackaging ? BOX_CURRENT_PRICE_CLASS : 'font-bold')}
                          style={resolveBoxCurrentPriceStyle(priceValueStyle, isBoxPackaging)}
                        >{priceLabel}</span>
                        {isBoxPackaging ? (
                          <span className="text-xs font-semibold uppercase tracking-wide" style={priceHintStyle}>
                            {t('product.unit_price_suffix')}
                          </span>
                        ) : null}
                      </div>
                      {isDistributionPriceActive ? (
                        <div className="pt-1">
                          <span className={PRICE_PANEL_BADGE_CLASS} style={priceBadgeStyle}>
                            {distributionPriceBadgeLabel}
                          </span>
                        </div>
                      ) : null}
                      </div>
                      {potentialPriceHint ? (
                        <p className="text-xs font-medium leading-relaxed lg:text-right" style={priceHintStyle}>
                          {potentialPriceHint}
                        </p>
                      ) : null}
                    </>
                  )}
                </div>
              ) : isPricePending ? (
                pendingPricePlaceholder
              ) : (
                <span className="text-sm text-muted-foreground/80 lg:w-[15rem] lg:text-right">-</span>
              )}
            </>
          ) : canDisplayGuestPricePrompt ? (
            <div className="flex w-full flex-col gap-1 lg:w-[15rem] lg:items-end">
              {!hideLoginBadge && (
                <Badge variant="outline" className="w-fit text-xs">
                  {t('product.price_after_login')}
                </Badge>
              )}
              <p className="text-xs text-muted-foreground lg:text-right">
                {t('product.login_to_see_prices')}
              </p>
            </div>
          ) : (
            <div className="hidden lg:block lg:w-[15rem]" aria-hidden="true" />
          )}

          {canAddToCart ? (
            <Button
              size="sm"
              className="h-10 w-full gap-2 rounded-xl border px-6 font-semibold transition-opacity duration-200 hover:opacity-90 lg:w-[15rem]"
              style={primaryActionStyle}
              onClick={handlePrimaryAction}
              disabled={!requiresVariationSelection && (!stockDisplay.available || isPricePending)}
            >
              {requiresVariationSelection ? (
                <ArrowRight className="h-4 w-4" />
              ) : (
                <ShoppingCart className="h-4 w-4" />
              )}
              {primaryActionLabel}
            </Button>
          ) : null}
        </div>
      </div>
    </Card>
  )

  if (isLinkedCard && linkHref) {
    return (
    <a
      href={linkHref}
      className="block h-full"
      aria-label={product.name}
      onClick={(event) => handleLinkClick(event, handleProductCardClick)}
    >
      {cardBody}
    </a>
    )
  }

  return cardBody
}
